import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { api } from "../../lib/api";
import { publicAssetUrl } from "../../lib/adminApi";

const statusOptions = ["published", "draft", "archived"];

function formatDate(value) {
  if (!value) return "No date";
  return new Date(value).toLocaleDateString();
}

export default function AdminJournalIssueList() {
  const [issues, setIssues] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (status) params.set("status", status);
    try {
      const data = await api(`/api/admin/journal-issues${params.toString() ? `?${params}` : ""}`);
      setIssues(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const stats = useMemo(() => issues.reduce((acc, item) => {
    acc.total += 1;
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, { total: 0, published: 0, draft: 0, archived: 0 }), [issues]);

  async function updateStatus(id, nextStatus) {
    setMessage("");
    setError("");
    try {
      await api(`/api/admin/journal-issues/${id}/status`, { method: "PATCH", body: JSON.stringify({ status: nextStatus }) });
      setMessage("Journal issue status updated.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteIssue(id) {
    if (!window.confirm("Delete this issue and all articles under it?")) return;
    setMessage("");
    setError("");
    try {
      await api(`/api/admin/journal-issues/${id}`, { method: "DELETE" });
      setMessage("Journal issue deleted.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <AdminLayout
      title="Journal Issues CMS"
      subtitle="Manage journal volumes, issues, covers, ISSN details and publication status."
      actions={<Link to="/adxm/journal-issues/create" className="rounded-xl bg-emerald-700 px-4 py-3 text-sm font-bold text-white">Add Issue</Link>}
    >
      <div className="grid gap-4 md:grid-cols-4">
        {[["Total", stats.total], ["Published", stats.published], ["Draft", stats.draft], ["Archived", stats.archived]].map(([label, value]) => (
          <div key={label} className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <p className="text-sm font-bold text-slate-500">{label}</p>
            <p className="mt-2 text-3xl font-black text-slate-950">{value || 0}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="grid gap-3 md:grid-cols-[1fr_220px_120px]">
          <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search title, volume, issue, ISSN..." className="rounded-xl border border-slate-300 px-4 py-3" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-300 px-4 py-3">
            <option value="">All status</option>
            {statusOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <button onClick={load} className="rounded-xl bg-slate-950 px-4 py-3 font-bold text-white">Search</button>
        </div>
      </div>

      {message && <div className="mt-6 rounded-xl bg-emerald-50 p-4 font-semibold text-emerald-700">{message}</div>}
      {error && <div className="mt-6 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}

      <div className="mt-6 overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="p-4">Issue</th>
                <th className="p-4">Volume</th>
                <th className="p-4">Date</th>
                <th className="p-4">Articles</th>
                <th className="p-4">Status</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="p-6" colSpan="6">Loading journal issues...</td></tr>
              ) : issues.length === 0 ? (
                <tr><td className="p-6 text-center text-slate-500" colSpan="6">No journal issues found.</td></tr>
              ) : issues.map((issue) => (
                <tr key={issue.id} className="border-t border-slate-100 align-top">
                  <td className="p-4">
                    <div className="flex gap-3">
                      <div className="h-16 w-16 overflow-hidden rounded-xl bg-slate-100">
                        {issue.cover_image && <img src={publicAssetUrl(issue.cover_image)} alt="" className="h-full w-full object-cover" />}
                      </div>
                      <div>
                        <p className="font-black text-slate-950">{issue.title}</p>
                        <p className="mt-1 text-xs text-slate-500">ISSN: {issue.issn || "N/A"}</p>
                        {Number(issue.is_featured) === 1 && <span className="mt-2 inline-block rounded-full bg-yellow-100 px-2 py-1 text-xs font-bold text-yellow-800">Featured</span>}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">{issue.volume || "-"}<br />{issue.issue || ""}</td>
                  <td className="p-4">{formatDate(issue.publication_date)}</td>
                  <td className="p-4 font-bold">{issue.article_count || 0}</td>
                  <td className="p-4">
                    <select value={issue.status} onChange={(e) => updateStatus(issue.id, e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2">
                      {statusOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/journals/${issue.slug}`} className="rounded-lg bg-slate-100 px-3 py-2 font-bold text-slate-700">View</Link>
                      <Link to={`/adxm/journal-issues/edit/${issue.id}`} className="rounded-lg bg-blue-50 px-3 py-2 font-bold text-blue-700">Edit</Link>
                      <Link to={`/adxm/journal-articles/create?issue_id=${issue.id}`} className="rounded-lg bg-emerald-50 px-3 py-2 font-bold text-emerald-700">Add Article</Link>
                      <button onClick={() => deleteIssue(issue.id)} className="rounded-lg bg-red-50 px-3 py-2 font-bold text-red-700">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
